package com.siriusxm.pia.views.unifiedaggregator.management

import androidx.compose.runtime.*
import com.siriusxm.pia.components.*
import com.siriusxm.pia.showError
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import unifiedcontent.aggregator.ServiceUser
import unifiedcontent.aggregator.ServiceUserRole
import unifiedcontent.aggregator.ServiceUsersList
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

enum class ServiceUsersType {
    CONSUMER,
    PRODUCER
}

/**
 * Displays the service users and allows for adding/removing.
 */
@Composable
fun ServiceUsersBox(
    aggregator: AggregatorService, serviceId: String,
    type: ServiceUsersType
) {

    var editing by remember { mutableStateOf<ServiceUser?>(null) }
    var users by remember { mutableStateOf<ServiceUsersList?>(null) }

    val canEdit by remember(aggregator, type, users, serviceId) {
        derivedStateOf {
            val hasRole = hasAdminRoleForCurrentUser(aggregator, users)
            aggregator.isAdmin() || hasRole
        }
    }

    LaunchedEffect(serviceId, type) {
        users = null
        users = when (type) {
            ServiceUsersType.CONSUMER -> aggregator.clientApi.getConsumerUsers(serviceId)
            ServiceUsersType.PRODUCER -> aggregator.clientApi.getProducerUsers(serviceId)
        }
    }

    suspend fun deleteUser(userId: String) {
        when (type) {
            ServiceUsersType.CONSUMER -> {
                aggregator.clientApi.deleteConsumerUser(serviceId, userId)
            }

            ServiceUsersType.PRODUCER ->
                aggregator.clientApi.deleteProducerUser(serviceId, userId)
        }
    }

    if (users != null && (aggregator.isAdmin() || !users.isNullOrEmpty())) {
        box("Authorized User Agents", {
            header({
                instruction = "These users can act as agents of this service within the Management Console."
            })
            paddedContent = false

            if (canEdit) {
                menu {
                    subAction {
                        title = "Add"
                        action {
                            editing = ServiceUser("")
                        }
                    }
                }
            }

        }) {
            editing?.let {
                Div({ style { padding(10.px) } }) {
                    serviceUserEditor(aggregator, it, serviceId, type) { updated ->
                        if (updated != null) {
                            users = users.orEmpty().replaceOrAppend(updated) { o1, o2 ->
                                o1.id == o2.id
                            }
                        }
                        editing = null
                    }
                }
            }

            if (users.isNullOrEmpty()) {
                if (editing == null) {
                    boxMessage("This service has no agents.")
                }
            } else {
                table<ServiceUser> {
                    items(users)

                    column {
                        content { user ->
                            val isCurrentUser = user.id.trim().lowercase() == aggregator.viewer.username
                            if (isCurrentUser) {
                                A(href = "#account", {
                                    onClick { e ->
                                        e.preventDefault()
                                        aggregator.globalNavigator.navigate("account")
                                    }
                                }) {
                                    Text(user.id)
                                }
                            } else {
                                Text(user.id)
                            }
                        }
                    }

                    column {
                        width = 50.px
                        content { user ->
                            Div({
                                style {
                                    display(DisplayStyle.Flex)
                                    gap(8.px)
                                    justifyContent(JustifyContent.FlexEnd)
                                }
                            }) {
                                if (user.roles?.contains(ServiceUserRole.ADMIN) == true) {
                                    svgIcon(SVGIcons.shield) {
                                        size = 16.px
                                        title = "Admin"
                                    }
                                }
                                if (canEdit) {
                                    svgIcon(SVGIcons.trash) {
                                        size = 16.px
                                        title = "Delete"
                                        action {
                                            deleteUser(user.id)
                                            users = users.orEmpty().filter { it.id != user.id }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

/**
 * True if the current user has ADMIN role in this service's user list.
 * Works for both producers and consumers.
 */
private fun hasAdminRoleForCurrentUser(
    aggregator: AggregatorService,
    users: ServiceUsersList?
): Boolean {
    val me = aggregator.viewer.username
    return users?.any { u ->
        u.id.trim().lowercase() == me &&
                u.roles?.contains(ServiceUserRole.ADMIN) == true
    } == true
}

/**
 * Editor for adding a new target to a consumer
 */
@Composable
fun serviceUserEditor(
    aggregator: AggregatorService, user: ServiceUser,
    serviceId: String,
    serviceType: ServiceUsersType,
    onComplete: suspend (ServiceUser?) -> Unit
) {
    var id by remember { mutableStateOf<String?>(user.id) }
    var roles by remember { mutableStateOf(user.roles.orEmpty()) }

    suspend fun saveUser(serviceUser: ServiceUser) {
        try {
            when (serviceType) {
                ServiceUsersType.CONSUMER -> {
                    aggregator.clientApi.updateConsumerUser(serviceId, serviceUser.id, serviceUser)
                }

                ServiceUsersType.PRODUCER -> {
                    aggregator.clientApi.updateProducerUser(serviceId, serviceUser.id, serviceUser)
                }
            }
            onComplete(serviceUser)
        } catch (e: Throwable) {
            aggregator.context.showError("Unable to add user", e)
        }
    }

    dialogView("Add user") {
        content {
            dialogField("User id", "The user's id (the part of their email before the @)") {
                simpleTextField(id) {
                    id = it
                }
            }
            dialogField("Roles") {
                listOf(ServiceUserRole.ADMIN).forEach { role ->
                    simpleCheckBox("Admin", roles.contains(role), true) {
                        if (it) {
                            roles = (roles + role).distinct()
                        } else {
                            roles = roles - role
                        }
                    }
                }
            }
        }

        action {
            title = "Add"
            showProgressOnAction = true
            enabled = id.orEmpty().length > 5
            this.action {
                val validatedId = id?.substringBefore('@').orEmpty()
                if (validatedId.length > 4) {
                    saveUser(ServiceUser(validatedId, roles))
                }
            }
        }
        action {
            title = "Cancel"
            enabled = true
            this.action {
                onComplete(null)
            }
        }
    }
}

/**
 * Replace the item in the list (matching using the provided function), or
 * append it to the end.
 */
fun <T : Any> List<T>.replaceOrAppend(newItem: T, match: (T, T) -> Boolean): List<T> {
    return if (this.any { match(it, newItem) }) {
        this.map { if (match(it, newItem)) newItem else it }
    } else {
        this + newItem
    }
}